/**
 * Shared chart styling.
 *
 * The React app draws with `@mantine/charts` (Recharts underneath); the Alpine
 * app it replaces draws with ApexCharts. The Alpine charts read as noticeably
 * more finished, and it is worth being precise about why, because the reflex
 * answer ("use a nicer library") is the wrong one:
 *
 *   - Alpine paints single-series charts in the near-black house accent
 *     (#161517). React painted them primary blue, which reads as a generic
 *     dashboard rather than as this product.
 *   - Alpine hides point markers at rest and shows them on hover. Mantine draws
 *     a dot on every point by default, which speckles a 90-day series.
 *   - Alpine's grid is a barely-there rgba(0,0,0,0.06) with no axis line and no
 *     tick marks. Mantine's defaults are heavier and box the plot in.
 *   - Alpine's axis labels are 11px at 45% ink. Mantine's are larger and darker,
 *     so the furniture competes with the data.
 *   - Alpine rounds bar ends and holds them to ~60% of the slot. Mantine draws
 *     full-width square bars.
 *   - Alpine formats the money axis as "£1.2k". React printed "1200".
 *
 * Since the 2026-10 refresh the reference is the marketing site rather than
 * the Alpine app: Geist Mono ticks, a white hairline tooltip instead of a dark
 * one, a fainter gradient, a draw-in on mount, and green (#3fb68b) as the one
 * positive hue against otherwise monochrome charts.
 *
 * Every one of those is a prop, not a library. So this file holds the house
 * chart style once and each screen spreads it, rather than six screens
 * re-deciding — the same reasoning as `theme.ts`, for the same reason: the old
 * app hand-wrote these per element and they drifted.
 *
 * Colour ASSIGNMENT (which hue is series 3) lives in `theme.ts` as
 * `CHART_SERIES`, because it is a palette decision and is validated as one.
 */
import { FONTS, TOKENS } from './theme';
import { prefersReducedMotion } from './ui/motion';

/**
 * Single-series charts are painted in the house near-black, not in a hue.
 * A lone series carries no identity information, so colour would be
 * decoration; the title already names it. Reserve the categorical hues in
 * `CHART_SERIES` for charts that genuinely encode identity.
 *
 * Every chart colour here is a CSS variable from tokens.css, so it follows
 * the scheme: in dark the ink is a soft #cfd0d4 rather than near-black on a
 * dark page. Recharts passes these straight into SVG fills and strokes,
 * where `var()` works as it does anywhere else.
 */
export const CHART_INK = 'var(--rs-chart-ink)';

/**
 * The one hue, as on the marketing site: money, sold, delivered, "now".
 * Use it for at most one series per chart, or it stops meaning anything.
 */
export const CHART_POSITIVE = TOKENS.green;

/** Chart furniture. Deliberately quieter than the data sitting on top of it. */
export const CHART_GRID = 'var(--rs-chart-grid)';
export const CHART_AXIS_TEXT = 'var(--rs-chart-axis)';

/** Geist Mono ticks in muted grey, as in the site's mockups. */
export const AXIS_TICK = {
  fontSize: 10.5,
  fill: CHART_AXIS_TEXT,
  fontFamily: FONTS.mono,
} as const;

/**
 * A monochrome scale for categorical breakdowns, strongest first: darkest
 * first in light, lightest first in dark. Paired with
 * CHART_POSITIVE for the one category that means "good" (delivered, sold),
 * it replaces the rainbow: every row is labelled, so hue is not needed to
 * tell them apart, and leaving it out keeps green meaningful.
 */
export const MONO_SCALE = [
  'var(--rs-mono-1)', 'var(--rs-mono-2)', 'var(--rs-mono-3)',
  'var(--rs-mono-4)', 'var(--rs-mono-5)', 'var(--rs-mono-6)',
] as const;

/** A quiet grey bar, for a row that is there for completeness. */
export const MONO_MUTED = 'var(--rs-mono-muted)';

/**
 * Draw-in timing for Recharts series. Long enough to read as the chart being
 * drawn, short enough to be over before anyone waits for it. Reduced motion
 * turns it off, and Recharts then renders the final frame.
 */
export const CHART_ANIMATION_MS = 900;

export function chartAnimation(reduced: boolean) {
  return {
    isAnimationActive: !reduced,
    animationDuration: CHART_ANIMATION_MS,
    animationEasing: 'ease-out' as const,
  };
}

/**
 * Raw Recharts axis presets, for charts drawn with Recharts directly (the
 * dashboard's combined leads and revenue chart, the hourly bars). Mantine
 * charts get the same values through the *_CHART_DEFAULTS below.
 */
export const X_AXIS_PROPS = {
  tick: AXIS_TICK,
  axisLine: false,
  tickLine: false,
  tickMargin: 10,
  minTickGap: 20,
} as const;

export const Y_AXIS_PROPS = {
  tick: AXIS_TICK,
  axisLine: false,
  tickLine: false,
  width: 44,
  tickMargin: 6,
} as const;

export const GRID_PROPS = {
  vertical: false,
  stroke: CHART_GRID,
} as const;

/** Hover cursors: one hairline for line and area charts, a pale band for bars. */
export const LINE_CURSOR = { stroke: TOKENS.borderStrong, strokeWidth: 1 } as const;
export const BAR_CURSOR = { fill: TOKENS.hoverTint } as const;

/**
 * Tooltip styles for Mantine charts: a floating card with a hairline, the x
 * value as a mono label and figures in tabular mono, matching `ChartTooltip`
 * in src/ui for charts drawn with Recharts directly.
 *
 * This replaces the earlier dark tooltip. A dark box read instantly, but it
 * was the one heavy object on otherwise hairline screens, and the site's own
 * mockups never use one.
 */
export const CHART_TOOLTIP_STYLES = {
  tooltip: {
    background: TOKENS.surface3,
    border: `1px solid ${TOKENS.borderStrong}`,
    borderRadius: 10,
    boxShadow: TOKENS.shadowPop,
    padding: '9px 12px 10px',
  },
  tooltipLabel: {
    fontFamily: FONTS.mono,
    color: TOKENS.textMuted,
    fontSize: 10.5,
    fontWeight: 500,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  tooltipItemName: { color: TOKENS.textSecondary, fontSize: 12 },
  tooltipItemData: {
    fontFamily: FONTS.mono,
    color: TOKENS.text,
    fontSize: 12.5,
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
} as const;

/*
  Mantine's chart defaults are module constants, so reduced motion is read
  once at load. Recharts animates only on mount and on data change, so a
  user who flips the setting mid-session sees at most one more draw-in.
*/
const MANTINE_SERIES_MOTION = chartAnimation(prefersReducedMotion());

/**
 * Grid and axis treatment shared by every cartesian chart.
 *
 * `gridAxis: 'y'` gives horizontal rules only. Vertical grid lines on a time
 * axis imply the gaps between dates are meaningful and mostly just add ink.
 */
const cartesianBase = {
  gridAxis: 'y',
  tickLine: 'none',
  gridColor: CHART_GRID,
  textColor: CHART_AXIS_TEXT,
  withTooltip: true,
  xAxisProps: { tick: AXIS_TICK, axisLine: false, tickMargin: 10, minTickGap: 20 },
  yAxisProps: { tick: AXIS_TICK, axisLine: false, width: 48 },
  styles: CHART_TOOLTIP_STYLES,
} as const;

/**
 * Area charts: smooth line, soft vertical gradient, no resting dots.
 *
 * The fill is there to give the line a body, not to be read as a quantity,
 * so it starts faint and fades out well before the axis.
 */
export const AREA_CHART_DEFAULTS = {
  ...cartesianBase,
  curveType: 'monotone',
  strokeWidth: 1.75,
  withGradient: true,
  withDots: false,
  activeDotProps: { r: 3.5, strokeWidth: 2, stroke: TOKENS.surface },
  fillOpacity: 0.16,
  areaProps: MANTINE_SERIES_MOTION,
} as const;

/**
 * Bar charts: rounded top corners, held off full width so the bars read as
 * discrete marks with the surface showing between them.
 */
export const BAR_CHART_DEFAULTS = {
  ...cartesianBase,
  barProps: {
    radius: [4, 4, 0, 0] as [number, number, number, number],
    ...MANTINE_SERIES_MOTION,
  },
  maxBarWidth: 38,
} as const;

/** Money on an axis, where "£12,400" is too wide to repeat six times. */
export function formatCompactGBP(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `£${(value / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `£${(value / 1_000).toFixed(1)}k`;
  return `£${Math.round(value)}`;
}

/** Counts on an axis. Same reasoning as `formatCompactGBP`. */
export function formatCompactNumber(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}m`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return `${Math.round(value)}`;
}
